import {PersonAdd, PersonRemove} from "@mui/icons-material";
import {Divider, Fab, Stack, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {useList} from "react-firebase-hooks/database";
import type {BaseExpenses, BasePayers} from "../../firebase/index.js";
import {auth, createExpendituresHookQuery, ExpenditureModel, ExpenseModel, formatCurrency, generateStartEndDateTexts, PayerModel, TripModel} from "../../firebase/index.js";
import {useInMemoryTrip} from "../../redux/InMemoryTripReducer.js";
import APXColouredButton from "../../reusables/APXColouredButton.jsx";
import APXWebAppBackButton from "../../reusables/APXWebAppBackButton.jsx";
import {webappPaths} from "../../reusables/js/AppRoutes.js";
import {expense_green_600, expense_red_600} from "../../reusables/js/APXTheme.js";
import AddNewExpenseBSL from "./AddNewExpenseBSL.jsx";
import AddNewPayerBSL from "./AddNewPayerBSL.jsx";
import AddNewReimbursementBSL from "./AddNewReimbursementBSL.jsx";
import ExpenseRow from "./ExpenseRow.jsx";
import FreezePayerBSL from "./FreezePayerBSL.jsx";
import PayerDetailsRow from "./PayerDetailsRow.jsx";


export default function ExpenditureMainFragment() {
	  const dbP: BasePayers = {};
	  const dbE: BaseExpenses = {};
	  const [payers, setPayers] = useState(dbP);
	  const [expenses, setExpenses] = useState(dbE);
	  
	  const [totalPersons, setTotalPersons] = useState(0);
	  const [totalExpense, setTotalExpense] = useState(0);
	  
	  const inMemoryTrip: TripModel = useInMemoryTrip();
	  
	  const [user] = useAuthState(auth);
	  const uid = /*TRUTHY->*/ !!user && user?.uid;
	  
	  const [snapshots, loading, error] = useList(createExpendituresHookQuery(uid, inMemoryTrip.expenditureKey));
	  
	  useEffect(() => {
			 if (/*TRUTHY->*/ !!snapshots && /*NOT->*/ !loading) {
					snapshots.map((snapshot) => {
						  const expenditure: ExpenditureModel = ExpenditureModel.fromObject(snapshot.val());
						  const bp: BasePayers = dbP;
						  if (/*TRUTHY->*/ !!expenditure.payers) {
								 Object.keys(expenditure.payers).forEach((key) => {
										bp[key] = PayerModel.fromObject(expenditure.payers[key]);
								 });
						  }
						  const be: BaseExpenses = dbE;
						  if (/*TRUTHY->*/ !!expenditure.expenses) {
								 Object.keys(expenditure.expenses).forEach((key) => {
										be[key] = ExpenseModel.fromObject(expenditure.expenses[key]);
								 });
						  }
						  setPayers(bp);
						  setExpenses(be);
						  setTotalPersons(expenditure.getTotalPersons());
						  setTotalExpense(expenditure.getTotalExpense());
					});
			 }
	  }, [snapshots]);
	  
	  const [openReimbursementBSL, setOpenReimbursementBSL] = useState(false);
	  const [openExpenseBSL, setOpenExpenseBSL] = useState(false);
	  
	  const [openAddNewPayerBSL, setOpenAddNewPayerBSL] = useState(false);
	  const [openFreezePayerBSL, setOpenFreezePayerBSL] = useState(false);
	  
	  return (
				 <>
						<APXWebAppBackButton webAppRoutePath={webappPaths.root}/>
						<Stack spacing={2}>
							  <Typography variant="h4">{inMemoryTrip.tripTo}</Typography>
							  <Typography variant="h5">Dates: {generateStartEndDateTexts(inMemoryTrip)}</Typography>
							  <Stack direction="row" spacing={2} alignItems="center">
									 <Typography variant="h5">Persons: {totalPersons}</Typography>
									 <Fab size={"small"}
											style={{backgroundColor: expense_green_600, color: "white"}}
											sx={{visibility: inMemoryTrip.isCompleted ? "hidden" : "visible"}}
											onClick={() => setOpenAddNewPayerBSL(true)}>
											<PersonAdd/>
									 </Fab>
									 <Fab size={"small"}
											style={{backgroundColor: expense_red_600, color: "white"}}
											sx={{visibility: inMemoryTrip.isCompleted ? "hidden" : "visible"}}
											onClick={() => setOpenFreezePayerBSL(true)}>
											<PersonRemove/>
									 </Fab>
							  </Stack>
						</Stack>
						<Divider sx={{my: 2}}/>
						{
							  Object.keys(payers).map((key) => {
									 const payer: PayerModel = payers[key];
									 return (
												<PayerDetailsRow key={key} item={payer}/>
									 );
							  })
						}
						<Divider sx={{my: 2}}/>
						{
							  Object.keys(expenses).map((key) => {
									 const expense: ExpenseModel = expenses[key];
									 return (
												<ExpenseRow key={key} item={expense}/>
									 );
							  })
						}
						<Divider sx={{my: 2}}/>
						<Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between" alignItems="center" sx={{mt: 2}}>
							  <Typography variant={"h4"}>Expenses until now:</Typography>
							  <Typography variant={"h4"} sx={{color: expense_red_600}}>{formatCurrency(totalExpense)}</Typography>
						</Stack>
						<Stack direction="row" spacing={2} justifyContent="center" sx={{mt: 2}}>
							  <APXColouredButton defaultDisabled={inMemoryTrip.isCompleted} text={"Reimbursement"} backgroundColour={expense_green_600} hoveredColour={"darkgreen"}
														onClick={() => setOpenReimbursementBSL(true)}/>
							  <APXColouredButton defaultDisabled={inMemoryTrip.isCompleted} text={"Expense"} backgroundColour={expense_red_600} hoveredColour={"darkred"}
														onClick={() => setOpenExpenseBSL(true)}/>
						</Stack>
						{openReimbursementBSL && <AddNewReimbursementBSL onClose={() => setOpenReimbursementBSL(false)} uid={uid} payers={payers}/>}
						{openExpenseBSL && <AddNewExpenseBSL onClose={() => setOpenExpenseBSL(false)} uid={uid} payers={payers} totalPersons={totalPersons}/>}
						{openAddNewPayerBSL && <AddNewPayerBSL uid={uid} onClose={() => setOpenAddNewPayerBSL(false)}/>}
						{openFreezePayerBSL && <FreezePayerBSL uid={uid} onClose={() => setOpenFreezePayerBSL(false)} payers={payers}/>}
				 </>
	  );
}